import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usersAPI } from '../api';
import {
  Gauge,
  EnvelopeOpen,
  ShieldCheck,
  GitBranch,
  Eye,
  CreditCard,
  ChartPieSlice,
  ChartLineUp,
  HardDrives,
  SlidersHorizontal,
  Buildings,
  ChartBar,
  GearSix,
  SignOut,
  List as HamburgerMenu,
  X,
  Shield,
  Calendar,
  Clock,
  Sun,
  Moon,
  CaretDown,
  CaretRight,
  Camera,
  FloppyDisk,
  Trash,
  WarningCircle,
  Check,
  Sparkle,
  Lock,
  User,
  Key,
  Envelope,
  EyeSlash as EyeOff,
  Lightning,
  DotsThreeOutline,
  ClipboardText,
} from '@phosphor-icons/react';

const SuperAdminLayout = ({ children }) => {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [darkMode, setDarkMode] = useState(() => {
    const saved = localStorage.getItem('pos-dark-mode');
    return saved ? JSON.parse(saved) : false;
  });
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);

  // Toggle dark class on html element for Tailwind dark mode
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('pos-dark-mode', JSON.stringify(darkMode));
  }, [darkMode]);

  const [currentTime, setCurrentTime] = useState(new Date());

  // Profile modal state
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileData, setProfileData] = useState({
    fullName: '',
    username: '',
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('profile');
  const profileImageRef = useRef(null);

  // Update time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  // Profile handlers
  const openProfileModal = () => {
    setProfileData({
      fullName: user?.fullName || '',
      username: user?.username || '',
      email: user?.email || '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
    setProfileImage(null);
    setProfileImagePreview(user?.profileImage || null);
    setProfileError('');
    setProfileSuccess('');
    setActiveTab('profile');
    setShowProfileModal(true);
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setProfileError('Image must be less than 5MB');
        return;
      }
      setProfileImage(file);
      setProfileImagePreview(URL.createObjectURL(file));
    }
  };

  const handleUploadProfileImage = async () => {
    if (!profileImage) return;
    setProfileLoading(true);
    setProfileError('');
    try {
      const formData = new FormData();
      formData.append('image', profileImage);
      await usersAPI.uploadProfileImage(formData);
      setProfileSuccess('Profile image updated successfully!');
      setProfileImage(null);
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (error) {
      setProfileError(error.response?.data?.error || 'Failed to upload image');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleDeleteProfileImage = async () => {
    setProfileLoading(true);
    setProfileError('');
    try {
      await usersAPI.deleteProfileImage();
      setProfileImagePreview(null);
      setProfileSuccess('Profile image removed');
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (error) {
      setProfileError(error.response?.data?.error || 'Failed to delete image');
    } finally {
      setProfileLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setProfileError('');
    setProfileSuccess('');

    if (profileData.newPassword) {
      if (!profileData.currentPassword) {
        setProfileError('Current password is required to change password');
        return;
      }
      if (profileData.newPassword !== profileData.confirmPassword) {
        setProfileError('New passwords do not match');
        return;
      }
      if (profileData.newPassword.length < 6) {
        setProfileError('New password must be at least 6 characters');
        return;
      }
    }

    setProfileLoading(true);
    try {
      const updateData = {};
      if (profileData.fullName !== user?.fullName) {
        updateData.fullName = profileData.fullName;
      }
      if (profileData.username !== user?.username) {
        updateData.username = profileData.username;
      }
      if (profileData.email !== (user?.email || '')) {
        updateData.email = profileData.email;
      }
      if (profileData.newPassword) {
        updateData.currentPassword = profileData.currentPassword;
        updateData.newPassword = profileData.newPassword;
      }

      if (Object.keys(updateData).length === 0) {
        setProfileError('No changes to save');
        setProfileLoading(false);
        return;
      }

      await usersAPI.updateProfile(updateData);
      await refreshUser(); // Refresh user data in context
      setProfileSuccess('Profile updated successfully!');
      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }));
      setTimeout(() => setProfileSuccess(''), 3000);
    } catch (error) {
      setProfileError(error.response?.data?.error || 'Failed to update profile');
    } finally {
      setProfileLoading(false);
    }
  };

  const isActive = (path) => location.pathname === path;

  // Icon wrappers for consistent phosphor rendering
  const CommandCenterIcon = (props) => <Gauge size={20} weight="bold" className={props.className} />;
  const ApplicationsIcon = (props) => <EnvelopeOpen size={20} weight="bold" className={props.className} />;
  const TenantGovIcon = (props) => <ShieldCheck size={20} weight="bold" className={props.className} />;
  const SubscriptionsIcon = (props) => <CreditCard size={20} weight="bold" className={props.className} />;
  const BranchReqIcon = (props) => <GitBranch size={20} weight="bold" className={props.className} />;
  const AnalyticsIcon = (props) => <ChartPieSlice size={20} weight="bold" className={props.className} />;
  const MarketIntIcon = (props) => <ChartLineUp size={20} weight="bold" className={props.className} />;
  const OversightIcon = (props) => <Eye size={20} weight="bold" className={props.className} />;
  const InfraIcon = (props) => <HardDrives size={20} weight="bold" className={props.className} />;
  const PlatformSettingsIcon = (props) => <SlidersHorizontal size={20} weight="bold" className={props.className} />;
  const AuditLogIcon = (props) => <ClipboardText size={20} weight="bold" className={props.className} />;
  // Group header icons
  const TenantsGroupIcon = (props) => <Buildings size={20} weight="bold" className={props.className} />;
  const AnalyticsGroupIcon = (props) => <ChartBar size={20} weight="bold" className={props.className} />;
  const PlatformGroupIcon = (props) => <GearSix size={20} weight="bold" className={props.className} />;

  const navGroups = [
    { label: null, links: [
      { path: '/admin/dashboard', label: 'Command Center', icon: CommandCenterIcon },
      { path: '/admin/applications', label: 'Applications', icon: ApplicationsIcon },
    ]},
    { label: 'Tenants', groupIcon: TenantsGroupIcon, links: [
      { path: '/admin/tenants', label: 'Tenant Governance', icon: TenantGovIcon },
      { path: '/admin/branch-requests', label: 'Branch Requests', icon: BranchReqIcon },
    ]},
    { label: 'Analytics', groupIcon: AnalyticsGroupIcon, links: [
      { path: '/admin/analytics', label: 'Global Analytics', icon: AnalyticsIcon },
      { path: '/admin/market-intelligence', label: 'Market Intelligence', icon: MarketIntIcon },
      { path: '/admin/oversight', label: 'Transaction Oversight', icon: OversightIcon },
    ]},
    { label: null, links: [
      { path: '/admin/platform', label: 'Infrastructure', icon: InfraIcon },
      { path: '/admin/audit-log', label: 'Audit Log', icon: AuditLogIcon },
    ]},
  ];

  const isGroupExpanded = (group) => {
    if (!group.label) return true;
    if (expandedGroups[group.label] !== undefined) return expandedGroups[group.label];
    return group.links.some(link => isActive(link.path));
  };

  const toggleGroup = (label) => {
    const group = navGroups.find(g => g.label === label);
    setExpandedGroups(prev => ({ ...prev, [label]: !isGroupExpanded(group) }));
  };

  const bgClass = darkMode ? 'bg-primaryB-900' : 'bg-primaryB-50';
  const surfaceClass = darkMode ? 'bg-primaryB-800' : 'bg-white';
  const textClass = darkMode ? 'text-white' : 'text-primaryB-900';
  const mutedClass = darkMode ? 'text-primaryB-400' : 'text-primaryB-500';
  const borderClass = darkMode ? 'border-primaryB-700/40' : 'border-primaryB-200/60';

  return (
    <div data-theme={darkMode ? 'dark' : 'light'} className={`h-screen-dvh overflow-hidden flex ${bgClass} ${textClass}`}>
      {/* Sidebar - Desktop (Full Height) */}
      <aside className={`hidden lg:flex ${sidebarCollapsed ? 'w-20' : 'w-64'} ${surfaceClass} shadow-card dark:shadow-card-dark flex-col shrink-0 transition-all duration-300 overflow-hidden`}>
        {/* Sidebar Top: Branding + Collapse */}
        <div className={`shrink-0 h-14 sm:h-16 border-b ${darkMode ? 'border-primaryB-700/40' : 'border-primaryB-200/60'} flex items-center justify-between ${sidebarCollapsed ? 'px-2' : 'px-4'}`}>
          <div className={`flex items-center ${sidebarCollapsed ? '' : 'gap-2.5'} min-w-0`}>
            <div className={`w-8 h-8 ${darkMode ? 'bg-accent-600' : 'bg-accent-600'} rounded-xl flex items-center justify-center shadow-card shrink-0`}>
              <Shield weight="bold" className="w-4 h-4 text-white" />
            </div>
            {!sidebarCollapsed && (
              <div className="min-w-0">
                <h1 className={`text-sm font-bold tracking-tight uppercase ${textClass} truncate`}>Platform Control</h1>
                <p className="text-[9px] font-bold text-accent-500 uppercase tracking-wider truncate mt-0.5">Super Administrator</p>
              </div>
            )}
          </div>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`shrink-0 p-1.5 rounded-xl ${mutedClass} hover:bg-primaryB-100 dark:hover:bg-primaryB-700 transition-all duration-200`}
            title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {sidebarCollapsed ? <CaretRight weight="bold" className="w-4 h-4" /> : <HamburgerMenu weight="bold" className="w-4 h-4" />}
          </button>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto py-3 px-2">
          {navGroups.map((group, groupIdx) => (
            <div key={groupIdx} className={groupIdx > 0 ? 'mt-1' : ''}>
              {!group.label ? (
                <div className="space-y-0.5">
                  {group.links.map((link) => {
                    const Icon = link.icon;
                    return (
                      <Link key={link.path} to={link.path}
                        className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2.5 rounded-2xl text-xs font-bold uppercase transition-all duration-200 ${
                          isActive(link.path)
                            ? darkMode ? 'bg-accent-500/15 text-accent-400 shadow-elevated-dark' : 'bg-accent-500 text-white shadow-elevated'
                            : `${mutedClass} ${darkMode ? 'hover:bg-primaryB-700 hover:text-white' : 'hover:bg-accent-50 hover:text-accent-700'}`
                        }`}
                        title={sidebarCollapsed ? link.label : ''}
                      >
                        <Icon className="w-5 h-5 shrink-0" />
                        {!sidebarCollapsed && <span className="truncate">{link.label}</span>}
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <>
                  {!sidebarCollapsed ? (
                    <>
                      <button onClick={() => toggleGroup(group.label)}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-2xl text-xs font-bold uppercase transition-all duration-200 ${
                          group.links.some(link => isActive(link.path))
                            ? darkMode ? 'text-accent-400' : `${textClass}`
                            : `${mutedClass} ${darkMode ? 'hover:bg-primaryB-700 hover:text-white' : 'hover:bg-accent-50 hover:text-accent-700'}`
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <group.groupIcon className="w-5 h-5 shrink-0" />
                          <span className="truncate">{group.label}</span>
                        </div>
                        <CaretDown weight="bold" className={`w-4 h-4 shrink-0 transition-transform duration-200 ${isGroupExpanded(group) ? 'rotate-180' : ''}`} />
                      </button>
                      {isGroupExpanded(group) && (
                        <div className="ml-6 mt-1">
                          {group.links.map((link) => (
                            <div
                              key={link.path}
                              className={`border-l-2 pl-3 ${
                                isActive(link.path)
                                  ? darkMode ? 'border-accent-400' : 'border-accent-600'
                                  : darkMode ? 'border-primaryB-700' : 'border-primaryB-200'
                              }`}
                            >
                              <Link
                                to={link.path}
                                className={`block px-3 py-2 text-xs font-bold uppercase transition-all duration-200 ${
                                  isActive(link.path)
                                    ? darkMode ? 'text-accent-400' : 'text-accent-700'
                                    : `${mutedClass} ${darkMode ? 'hover:text-white' : 'hover:text-accent-700'}`
                                }`}
                              >
                                {link.label}
                              </Link>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => setSidebarCollapsed(false)}
                      className={`w-full flex items-center justify-center px-3 py-2.5 rounded-2xl transition-all duration-200 ${
                        group.links.some(link => isActive(link.path))
                          ? darkMode ? 'text-accent-400' : `${textClass}`
                          : `${mutedClass} hover:bg-accent-50 dark:hover:bg-primaryB-700`
                      }`}
                      title={group.label}
                    >
                      <group.groupIcon className="w-5 h-5" />
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </nav>

        {/* Footer */}
        <div className={`shrink-0 border-t ${darkMode ? 'border-primaryB-700/40' : 'border-primaryB-200/60'} p-2.5 space-y-1`}>
          {!sidebarCollapsed && (
            <div className={`flex items-center rounded-2xl ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-100'} p-1 mb-1`}>
              <button
                onClick={() => setDarkMode(false)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl text-[10px] font-bold uppercase transition-all duration-200 ${!darkMode ? `${surfaceClass} shadow-card ${textClass}` : mutedClass}`}
              >
                <Sun weight="bold" className="w-3.5 h-3.5" />
                Light
              </button>
              <button
                onClick={() => setDarkMode(true)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-1.5 rounded-xl text-[10px] font-bold uppercase transition-all duration-200 ${darkMode ? `${surfaceClass} shadow-card ${textClass}` : mutedClass}`}
              >
                <Moon weight="bold" className="w-3.5 h-3.5" />
                Dark
              </button>
            </div>
          )}
          {sidebarCollapsed && (
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`w-full flex items-center justify-center p-2 rounded-2xl ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-100'} ${textClass} mb-1 transition-all duration-200`}
              title={darkMode ? 'Switch to Light' : 'Switch to Dark'}
            >
              {darkMode ? <Sun weight="bold" className="w-4 h-4" /> : <Moon weight="bold" className="w-4 h-4" />}
            </button>
          )}
          <button onClick={handleLogout}
            className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'} px-3 py-2 rounded-2xl text-xs font-bold uppercase ${mutedClass} hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all duration-200`}
            title={sidebarCollapsed ? 'Logout' : ''}
          >
            <SignOut weight="bold" className="w-5 h-5 shrink-0" />
            {!sidebarCollapsed && <span>Logout</span>}
          </button>
        </div>
      </aside>

      {/* Right Side: Header + Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header (right side only) */}
        <header className={`h-14 sm:h-16 ${darkMode ? 'bg-primaryB-900/80' : 'bg-primaryB-50/80'} backdrop-blur-xl border-b ${darkMode ? 'border-primaryB-700/40' : 'border-primaryB-200/60'} flex items-center justify-between px-4 sm:px-6 lg:px-8 shrink-0 z-10`}>
          <div className="flex items-center gap-3">
            <p className={`lg:hidden text-xs font-bold uppercase ${textClass}`}>SmartPOS</p>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            <div className={`hidden lg:flex items-center gap-3 px-4 py-2 ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-100'} rounded-2xl`}>
              <div className="flex items-center gap-1.5">
                <Calendar weight="bold" className={`w-3.5 h-3.5 ${mutedClass}`} />
                <span className={`text-[10px] font-bold uppercase ${textClass}`}>
                  {currentTime.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                </span>
              </div>
              <div className={`w-px h-4 ${borderClass}`}></div>
              <div className="flex items-center gap-1.5">
                <Clock weight="bold" className={`w-3.5 h-3.5 ${mutedClass}`} />
                <span className={`text-[10px] font-bold uppercase ${textClass}`}>
                  {currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
            <button
              onClick={() => setDarkMode(!darkMode)}
              className={`w-8 h-8 sm:w-10 sm:h-10 border ${borderClass} rounded-2xl hidden sm:flex items-center justify-center ${surfaceClass} ${textClass} transition-all duration-200`}
            >
              {darkMode ? <Sun weight="bold" className="w-4 h-4 sm:w-5 sm:h-5" /> : <Moon weight="bold" className="w-4 h-4 sm:w-5 sm:h-5" />}
            </button>
            <button
              onClick={openProfileModal}
              className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 ${darkMode ? 'border-accent-400' : 'border-accent-600'} bg-primaryB-100 overflow-hidden hover:ring-2 hover:ring-accent-300 transition-all duration-200`}
              title="Edit Profile"
            >
              {user?.profileImage ? (
                <img src={user.profileImage} alt={user.fullName} className="w-full h-full object-cover" />
              ) : (
                <div className={`w-full h-full ${darkMode ? 'bg-accent-600' : 'bg-accent-500'} flex items-center justify-center text-xs font-bold text-white`}>
                  {user?.fullName?.charAt(0) || 'S'}
                </div>
              )}
            </button>
          </div>
        </header>

        {/* Main Content Area */}
        <main id="main-content" className="flex-1 overflow-y-auto p-4 pb-32 sm:p-6 sm:pb-32 lg:p-8 lg:pb-8 page-gradient-warm">
          {React.cloneElement(children, {
            darkMode,
            surfaceClass,
            textClass,
            mutedClass,
            borderClass,
            bgClass
          })}
        </main>
      </div>

      {/* Mobile Bottom Tab Bar */}
      <nav className={`lg:hidden fixed bottom-0 left-0 right-0 z-30 ${surfaceClass} shadow-card dark:shadow-card-dark border-t ${borderClass} safe-area-bottom`}>
        <div className="flex items-center justify-around px-1 py-1.5">
          {[
            { path: '/admin/dashboard', icon: Gauge, label: 'Home' },
            { path: '/admin/applications', icon: EnvelopeOpen, label: 'Apps' },
            { path: '/admin/tenants', icon: Buildings, label: 'Tenants' },
            { path: 'more', icon: DotsThreeOutline, label: 'More', action: () => setMoreSheetOpen(true) },
          ].map((tab) => {
            const tabActive = tab.path !== 'more' && location.pathname === tab.path;
            return (
              <button
                key={tab.label}
                onClick={() => {
                  if (tab.action) {
                    tab.action();
                  } else {
                    navigate(tab.path);
                  }
                }}
                className={`flex flex-col items-center justify-center flex-1 py-1.5 rounded-2xl transition-all duration-200 ${
                  tabActive
                    ? darkMode ? 'bg-accent-500/15 text-accent-400' : 'bg-accent-500 text-white shadow-elevated'
                    : mutedClass
                }`}
              >
                <tab.icon weight={tabActive ? 'fill' : 'bold'} className="w-[22px] h-[22px]" />
                <span className={`text-[10px] font-bold mt-0.5 ${tabActive ? (darkMode ? 'text-accent-400' : 'text-white') : ''}`}>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </nav>

      {/* More Bottom Sheet */}
      {moreSheetOpen && (
        <div className="lg:hidden fixed inset-0 z-[60]" onClick={() => setMoreSheetOpen(false)}>
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            className={`absolute bottom-0 left-0 right-0 ${surfaceClass} rounded-t-3xl shadow-elevated dark:shadow-elevated-dark max-h-[70vh] overflow-y-auto safe-area-bottom`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-2">
              <div className={`w-10 h-1 rounded-full ${darkMode ? 'bg-primaryB-600' : 'bg-primaryB-300'}`} />
            </div>
            <div className="px-5 pb-6">
              <div className="grid grid-cols-4 gap-3 mb-4">
                {navGroups.flatMap(group => group.links).filter(link => {
                  const tabPaths = ['/admin/dashboard', '/admin/applications', '/admin/tenants'];
                  return !tabPaths.includes(link.path);
                }).map((link) => {
                  const Icon = link.icon;
                  return (
                    <button
                      key={link.path}
                      onClick={() => { setMoreSheetOpen(false); navigate(link.path); }}
                      className={`flex flex-col items-center gap-1.5 p-3 rounded-2xl transition-all duration-200 ${
                        isActive(link.path)
                          ? `${darkMode ? 'bg-accent-900/30' : 'bg-accent-50'} text-accent-500`
                          : `${darkMode ? 'hover:bg-primaryB-700' : 'hover:bg-primaryB-100'} ${mutedClass}`
                      }`}
                    >
                      <Icon weight="bold" className="w-5 h-5" />
                      <span className="text-[10px] font-bold text-center leading-tight">{link.label}</span>
                    </button>
                  );
                })}
              </div>
              <div className={`border-t ${borderClass} pt-3 space-y-2`}>
                <div className={`flex items-center rounded-2xl ${darkMode ? 'bg-primaryB-700' : 'bg-primaryB-100'} p-1`}>
                  <button
                    onClick={() => setDarkMode(false)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-[10px] font-bold uppercase transition-all duration-200 ${!darkMode ? `${surfaceClass} shadow-card ${textClass}` : mutedClass}`}
                  >
                    <Sun weight="bold" className="w-3.5 h-3.5" />
                    Light
                  </button>
                  <button
                    onClick={() => setDarkMode(true)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-[10px] font-bold uppercase transition-all duration-200 ${darkMode ? `${surfaceClass} shadow-card ${textClass}` : mutedClass}`}
                  >
                    <Moon weight="bold" className="w-3.5 h-3.5" />
                    Dark
                  </button>
                </div>
                <button
                  onClick={() => { setMoreSheetOpen(false); handleLogout(); }}
                  className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl text-xs font-bold uppercase text-red-500 ${darkMode ? 'hover:bg-red-900/20' : 'hover:bg-red-50'} transition-all duration-200`}
                >
                  <SignOut weight="bold" className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Premium SuperAdmin Profile Modal */}
      {showProfileModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 sm:p-6"
          onClick={() => setShowProfileModal(false)}
        >
          {/* Backdrop with blur */}
          <div className="absolute inset-0 bg-primaryB-900/70 backdrop-blur-md" />

          {/* Modal */}
          <div
            className={`relative w-full max-w-2xl ${surfaceClass} rounded-3xl shadow-elevated dark:shadow-elevated-dark border ${borderClass} max-h-[90vh] overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-300`}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Premium Header with Gradient */}
            <div className="relative overflow-hidden">
              {/* Background gradient */}
              <div className={`absolute inset-0 ${darkMode ? 'bg-gradient-to-br from-accent-600 via-purple-600 to-pink-500' : 'bg-gradient-to-br from-accent-500 via-purple-500 to-pink-400'}`} />
              <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzRjMC0yLjIxLTEuNzktNC00LTRzLTQgMS43OS00IDQgMS43OSA0IDQgNCA0LTEuNzkgNC00eiIvPjwvZz48L2c+PC9zdmc+')] opacity-30" />

              {/* Header content */}
              <div className="relative px-6 sm:px-8 pt-6 sm:pt-8 pb-20 sm:pb-24">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-3xl bg-primaryB-50/20 backdrop-blur-sm flex items-center justify-center">
                      <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">Admin Profile</h2>
                      <p className="text-white/70 text-xs sm:text-sm font-semibold">Platform Super Administrator</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowProfileModal(false)}
                    className="w-10 h-10 rounded-2xl bg-primaryB-50/10 hover:bg-primaryB-50/20 backdrop-blur-sm flex items-center justify-center text-white transition-all duration-200"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Decorative elements */}
                <div className="absolute top-4 right-24 w-20 h-20 bg-primaryB-50/10 rounded-full blur-2xl" />
                <div className="absolute bottom-8 left-8 w-32 h-32 bg-purple-400/20 rounded-full blur-3xl" />
              </div>

              {/* Profile Image - Overlapping */}
              <div className="absolute -bottom-16 sm:-bottom-20 left-1/2 -translate-x-1/2">
                <div className="relative group">
                  <div className={`w-32 h-32 sm:w-40 sm:h-40 rounded-3xl border-4 ${darkMode ? 'border-primaryB-800' : 'border-white'} shadow-elevated overflow-hidden bg-gradient-to-br from-accent-500 to-purple-600`}>
                    {profileImagePreview ? (
                      <img src={profileImagePreview} alt="Profile" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <span className="text-4xl sm:text-5xl font-bold text-white">
                          {user?.fullName?.charAt(0) || 'S'}
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Camera button */}
                  <button
                    onClick={() => profileImageRef.current?.click()}
                    className="absolute -bottom-2 -right-2 w-12 h-12 rounded-3xl bg-accent-600 hover:bg-accent-700 text-white shadow-elevated shadow-accent-600/50 flex items-center justify-center transition-all duration-200 hover:scale-110"
                  >
                    <Camera className="w-5 h-5" />
                  </button>
                  <input
                    type="file"
                    ref={profileImageRef}
                    onChange={handleProfileImageChange}
                    accept="image/*"
                    className="hidden"
                  />

                  {/* Sparkle decoration */}
                  <div className="absolute -top-2 -left-2 w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center shadow-elevated">
                    <Sparkle className="w-4 h-4 text-amber-900" />
                  </div>
                </div>
              </div>
            </div>

            {/* Content Area */}
            <div className="pt-20 sm:pt-24 px-4 sm:px-8 pb-6 sm:pb-8 overflow-y-auto flex-1">
              {/* Image action buttons */}
              {profileImage && (
                <div className="flex justify-center gap-3 mb-6">
                  <button
                    onClick={handleUploadProfileImage}
                    disabled={profileLoading}
                    className="px-5 py-2.5 bg-gradient-to-r from-accent-600 to-purple-600 hover:from-accent-700 hover:to-purple-700 text-white rounded-2xl text-sm font-bold shadow-elevated shadow-accent-600/25 disabled:opacity-50 flex items-center gap-2 transition-all duration-200"
                  >
                    <FloppyDisk className="w-4 h-4" />
                    {profileLoading ? 'Uploading...' : 'Save Image'}
                  </button>
                  <button
                    onClick={() => {
                      setProfileImage(null);
                      setProfileImagePreview(user?.profileImage || null);
                    }}
                    className={`px-5 py-2.5 ${surfaceClass} border ${borderClass} rounded-2xl text-sm font-bold ${textClass} hover:bg-primaryB-100 dark:hover:bg-primaryB-700 transition-all duration-200`}
                  >
                    Cancel
                  </button>
                </div>
              )}
              {profileImagePreview && !profileImage && user?.profileImage && (
                <div className="flex justify-center mb-6">
                  <button
                    onClick={handleDeleteProfileImage}
                    disabled={profileLoading}
                    className="px-4 py-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-2xl text-sm font-bold disabled:opacity-50 flex items-center gap-2 transition-all duration-200"
                  >
                    <Trash className="w-4 h-4" />
                    Remove Image
                  </button>
                </div>
              )}

              {/* Messages */}
              {profileError && (
                <div className="mb-6 p-4 rounded-3xl bg-red-100 dark:bg-red-900/30 border border-red-200 dark:border-red-800 flex items-center gap-3 animate-in slide-in-from-top duration-300">
                  <div className="w-10 h-10 rounded-2xl bg-red-500/20 flex items-center justify-center">
                    <WarningCircle className="w-5 h-5 text-red-500" />
                  </div>
                  <p className="text-sm font-semibold text-red-600 dark:text-red-400">{profileError}</p>
                </div>
              )}
              {profileSuccess && (
                <div className="mb-6 p-4 rounded-3xl bg-emerald-100 dark:bg-emerald-900/30 border border-emerald-200 dark:border-emerald-800 flex items-center gap-3 animate-in slide-in-from-top duration-300">
                  <div className="w-10 h-10 rounded-2xl bg-emerald-500/20 flex items-center justify-center">
                    <Check className="w-5 h-5 text-emerald-500" />
                  </div>
                  <p className="text-sm font-semibold text-emerald-600 dark:text-emerald-400">{profileSuccess}</p>
                </div>
              )}

              {/* Tab Navigation */}
              <div className={`flex gap-2 p-1.5 rounded-3xl ${darkMode ? 'bg-primaryB-700/50' : 'bg-primaryB-100'} mb-6`}>
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl text-sm font-bold uppercase tracking-wide transition-all duration-200 ${
                    activeTab === 'profile'
                      ? 'bg-gradient-to-r from-accent-600 to-purple-600 text-white shadow-elevated shadow-accent-600/25'
                      : `${textClass} hover:bg-primaryB-50 dark:hover:bg-primaryB-600`
                  }`}
                >
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">Profile Info</span>
                  <span className="sm:hidden">Profile</span>
                </button>
                <button
                  onClick={() => setActiveTab('security')}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-2xl text-sm font-bold uppercase tracking-wide transition-all duration-200 ${
                    activeTab === 'security'
                      ? 'bg-gradient-to-r from-accent-600 to-purple-600 text-white shadow-elevated shadow-accent-600/25'
                      : `${textClass} hover:bg-primaryB-50 dark:hover:bg-primaryB-600`
                  }`}
                >
                  <Lock className="w-4 h-4" />
                  <span className="hidden sm:inline">Security</span>
                  <span className="sm:hidden">Security</span>
                </button>
              </div>

              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="space-y-5 animate-in fade-in duration-300">
                  {/* Admin Badge */}
                  <div className={`p-4 rounded-3xl ${darkMode ? 'bg-gradient-to-r from-accent-900/50 to-purple-900/50 border-accent-700/50' : 'bg-gradient-to-r from-accent-50 to-purple-50 border-accent-200'} border flex items-center gap-4`}>
                    <div className={`w-12 h-12 rounded-3xl ${darkMode ? 'bg-accent-600' : 'bg-accent-500'} flex items-center justify-center`}>
                      <Lightning className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className={`text-sm font-bold uppercase ${darkMode ? 'text-accent-300' : 'text-accent-700'}`}>Super Administrator</p>
                      <p className={`text-xs ${mutedClass}`}>Full platform access & control</p>
                    </div>
                  </div>

                  {/* Full Name */}
                  <div>
                    <label className={`text-xs font-bold uppercase ${mutedClass} block mb-2 flex items-center gap-2`}>
                      <User className="w-3.5 h-3.5" />
                      Full Name
                    </label>
                    <input
                      type="text"
                      value={profileData.fullName}
                      onChange={(e) => setProfileData(prev => ({ ...prev, fullName: e.target.value }))}
                      className={`w-full px-4 py-3.5 rounded-xl border-2 ${borderClass} ${surfaceClass} ${textClass} focus:border-accent-500 focus:ring-4 focus:ring-accent-500/10 transition-all outline-none`}
                      placeholder="Enter your full name"
                    />
                  </div>

                  {/* Username */}
                  <div>
                    <label className={`text-xs font-bold uppercase ${mutedClass} block mb-2 flex items-center gap-2`}>
                      <span className="text-accent-500">@</span>
                      Username
                    </label>
                    <input
                      type="text"
                      value={profileData.username}
                      onChange={(e) => setProfileData(prev => ({ ...prev, username: e.target.value }))}
                      className={`w-full px-4 py-3.5 rounded-xl border-2 ${borderClass} ${surfaceClass} ${textClass} focus:border-accent-500 focus:ring-4 focus:ring-accent-500/10 transition-all outline-none`}
                      placeholder="Enter username"
                    />
                  </div>

                  {/* Email */}
                  <div>
                    <label className={`text-xs font-bold uppercase ${mutedClass} block mb-2 flex items-center gap-2`}>
                      <Envelope className="w-3.5 h-3.5" />
                      Email Address
                      <span className={`ml-auto text-[10px] px-2 py-0.5 rounded-full ${darkMode ? 'bg-accent-600/30 text-accent-300' : 'bg-accent-100 text-accent-600'}`}>For Password Recovery</span>
                    </label>
                    <input
                      type="email"
                      value={profileData.email}
                      onChange={(e) => setProfileData(prev => ({ ...prev, email: e.target.value }))}
                      className={`w-full px-4 py-3.5 rounded-xl border-2 ${borderClass} ${surfaceClass} ${textClass} focus:border-accent-500 focus:ring-4 focus:ring-accent-500/10 transition-all outline-none`}
                      placeholder="Enter email address"
                    />
                    <p className={`text-xs ${mutedClass} mt-1.5`}>
                      This email will be used to reset your password if you forget it.
                    </p>
                  </div>
                </div>
              )}

              {/* Security Tab */}
              {activeTab === 'security' && (
                <div className="space-y-5 animate-in fade-in duration-300">
                  {/* Security Notice */}
                  <div className={`p-4 rounded-3xl ${darkMode ? 'bg-amber-900/30 border-amber-700/50' : 'bg-amber-50 border-amber-200'} border flex items-start gap-4`}>
                    <div className={`w-10 h-10 rounded-2xl ${darkMode ? 'bg-amber-600/30' : 'bg-amber-100'} flex items-center justify-center shrink-0`}>
                      <Key className="w-5 h-5 text-amber-600" />
                    </div>
                    <div>
                      <p className={`text-sm font-bold ${darkMode ? 'text-amber-300' : 'text-amber-800'}`}>Password Security</p>
                      <p className={`text-xs ${darkMode ? 'text-amber-400/80' : 'text-amber-700'} mt-1`}>
                        Use a strong password with at least 6 characters. Your password protects full platform access.
                      </p>
                    </div>
                  </div>

                  {/* Current Password */}
                  <div>
                    <label className={`text-xs font-bold uppercase ${mutedClass} block mb-2`}>Current Password</label>
                    <div className="relative">
                      <input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={profileData.currentPassword}
                        onChange={(e) => setProfileData(prev => ({ ...prev, currentPassword: e.target.value }))}
                        className={`w-full px-4 py-3.5 pr-12 rounded-xl border-2 ${borderClass} ${surfaceClass} ${textClass} focus:border-accent-500 focus:ring-4 focus:ring-accent-500/10 transition-all outline-none`}
                        placeholder="Enter current password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-xl ${mutedClass} hover:bg-primaryB-100 dark:hover:bg-primaryB-700 transition-all duration-200`}
                      >
                        {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* New Password */}
                  <div>
                    <label className={`text-xs font-bold uppercase ${mutedClass} block mb-2`}>New Password</label>
                    <div className="relative">
                      <input
                        type={showNewPassword ? 'text' : 'password'}
                        value={profileData.newPassword}
                        onChange={(e) => setProfileData(prev => ({ ...prev, newPassword: e.target.value }))}
                        className={`w-full px-4 py-3.5 pr-12 rounded-xl border-2 ${borderClass} ${surfaceClass} ${textClass} focus:border-accent-500 focus:ring-4 focus:ring-accent-500/10 transition-all outline-none`}
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-xl ${mutedClass} hover:bg-primaryB-100 dark:hover:bg-primaryB-700 transition-all duration-200`}
                      >
                        {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                  {/* Confirm Password */}
                  <div>
                    <label className={`text-xs font-bold uppercase ${mutedClass} block mb-2`}>Confirm New Password</label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        value={profileData.confirmPassword}
                        onChange={(e) => setProfileData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                        className={`w-full px-4 py-3.5 pr-12 rounded-xl border-2 ${profileData.confirmPassword && profileData.newPassword !== profileData.confirmPassword ? 'border-red-500' : borderClass} ${surfaceClass} ${textClass} focus:border-accent-500 focus:ring-4 focus:ring-accent-500/10 transition-all outline-none`}
                        placeholder="Confirm new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 p-1.5 rounded-xl ${mutedClass} hover:bg-primaryB-100 dark:hover:bg-primaryB-700 transition-all duration-200`}
                      >
                        {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    {profileData.confirmPassword && profileData.newPassword !== profileData.confirmPassword && (
                      <p className="text-xs text-red-500 mt-1 flex items-center gap-1">
                        <WarningCircle className="w-3 h-3" />
                        Passwords do not match
                      </p>
                    )}
                    {profileData.confirmPassword && profileData.newPassword === profileData.confirmPassword && profileData.newPassword && (
                      <p className="text-xs text-emerald-500 mt-1 flex items-center gap-1">
                        <Check className="w-3 h-3" />
                        Passwords match
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className={`px-4 sm:px-8 py-4 sm:py-5 border-t ${borderClass} flex flex-col sm:flex-row items-center justify-end gap-3 ${darkMode ? 'bg-primaryB-800/50' : 'bg-primaryB-50'}`}>
              <button
                onClick={() => setShowProfileModal(false)}
                className={`w-full sm:w-auto px-6 py-3 ${surfaceClass} border ${borderClass} rounded-2xl text-sm font-bold ${textClass} hover:bg-primaryB-100 dark:hover:bg-primaryB-700 transition-all duration-200`}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveProfile}
                disabled={profileLoading}
                className="w-full sm:w-auto px-8 py-3 bg-gradient-to-r from-accent-600 to-purple-600 hover:from-accent-700 hover:to-purple-700 text-white rounded-2xl text-sm font-bold shadow-elevated shadow-accent-600/25 disabled:opacity-50 flex items-center justify-center gap-2 transition-all duration-200"
              >
                {profileLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <FloppyDisk className="w-4 h-4" />
                    Save Changes
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminLayout;
